import { haversineDistanceKm, isValidGeoCoord, isDesktopBrowser, lookupPincodeCoordinates, logShopGeo, parseCoord, validateIndianPincode, SHOP_GPS_UNRELIABLE_ACCURACY_M } from '../../utils/geo';
import { buildAccountTypeMeta } from './partnerSignupMeta';
import {
  normalizeGarageTier,
  validateClusterSelection,
  getBookingModeForTier,
  getClusterById,
  resolveFuelSpecialistTypes,
  fuelSpecialistSelectionRequired,
} from '../../constants/garageServiceClusters';
import { previewGarageServices } from '../../utils/garageServiceExpansion';

export const PARTNER_BUSINESS_DRAFT_KEY = 'partner_signup_business_draft';
export const PARTNER_SIGNUP_STEP_KEY = 'partner_signup_step';
export const PARTNER_SIGNUP_SUB_STEP_KEY = 'partner_signup_sub_step';
export const PARTNER_SIGNUP_SESSION_KEY = 'partner_signup_session_active';

export function markSignupSessionActive() {
  try {
    sessionStorage.setItem(PARTNER_SIGNUP_SESSION_KEY, '1');
  } catch {
    // ignore
  }
}

export function hasActiveSignupSession() {
  try {
    return sessionStorage.getItem(PARTNER_SIGNUP_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

/** Reset window + signup panel scroll after step / sub-step navigation. */
export function scrollSignupPanelToTop(anchorId) {
  if (typeof window === 'undefined') return;
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
  document.querySelectorAll('.partner-panel, .partner-signup-shell').forEach((el) => {
    el.scrollTop = 0;
  });
  const anchor = anchorId ? document.getElementById(anchorId) : null;
  if (anchor) {
    anchor.scrollIntoView({ block: 'start', behavior: 'auto' });
  }
}

export function loadSignupStep() {
  const step = parseInt(localStorage.getItem(PARTNER_SIGNUP_STEP_KEY), 10);
  return step >= 1 && step <= 3 ? step : 1;
}

export function saveSignupStep(step) {
  try {
    localStorage.setItem(PARTNER_SIGNUP_STEP_KEY, String(step));
  } catch {
    // ignore
  }
}

export function loadSignupSubStep() {
  const sub = parseInt(sessionStorage.getItem(PARTNER_SIGNUP_SUB_STEP_KEY), 10);
  return sub >= 0 && sub <= 2 ? sub : 0;
}

export function saveSignupSubStep(subStep) {
  try {
    sessionStorage.setItem(PARTNER_SIGNUP_SUB_STEP_KEY, String(subStep));
  } catch {
    // ignore
  }
}

export const COMPANY_TYPES = [
  {
    value: 'sole_proprietorship',
    label: 'Sole Proprietorship',
    desc: 'Most roadside garages & small sellers',
  },
  {
    value: 'partnership',
    label: 'Partnership Firm',
    desc: 'Common for family-run auto shops',
  },
  {
    value: 'private_limited',
    label: 'Private Limited (Pvt. Ltd.)',
    desc: 'Organized spare part distributors',
  },
  {
    value: 'llp',
    label: 'LLP',
    desc: 'Growing among mid-size workshops',
  },
  {
    value: 'opc',
    label: 'OPC',
    desc: 'Solo founders with formal registration',
  },
  {
    value: 'other',
    label: 'Other',
    desc: 'Unregistered or informal setups — GST, shop & street photos required',
  },
];

export const GARAGE_COMPANY_TYPES = [
  { value: 'small_garage', label: 'Small garage', desc: 'Local workshop — pick min. 2 service clusters' },
  { value: 'medium_garage', label: 'Medium garage', desc: 'Multi-bay service — instant book' },
  { value: 'large_garage', label: 'Large garage', desc: 'Full service center — all clusters by default' },
  { value: 'custom_garage', label: 'Custom workshop', desc: 'Tuning & performance — quote-based jobs' },
  { value: 'transformer', label: 'Transformer', desc: 'Full vehicle transformation — staged projects' },
];

export const GARAGE_SPECIFICATIONS = [
  { value: 'general_repair', label: 'General repair & servicing' },
  { value: 'periodic_service', label: 'Periodic / scheduled service' },
  { value: 'body_paint', label: 'Body work & painting' },
  { value: 'ac_electrical', label: 'AC & electrical' },
  { value: 'wheel_alignment', label: 'Wheel alignment & balancing' },
  { value: 'two_wheeler', label: 'Two-wheeler service' },
  { value: 'four_wheeler', label: 'Four-wheeler service' },
  { value: 'detailing', label: 'Detailing & car care' },
  { value: 'multi_brand', label: 'Multi-brand workshop' },
  { value: 'other', label: 'Other' },
];

export function getSpecificationsForAccount(mainType) {
  return mainType === 'garage' ? GARAGE_SPECIFICATIONS : BUSINESS_SPECIFICATIONS;
}

export function getCompanyTypesForAccount(mainType) {
  return mainType === 'garage' ? GARAGE_COMPANY_TYPES : COMPANY_TYPES;
}

export function getCompanyTypeOption(value) {
  return COMPANY_TYPES.find((t) => t.value === value)
    || GARAGE_COMPANY_TYPES.find((t) => t.value === value)
    || null;
}

export const BUSINESS_SPECIFICATIONS = [
  { value: 'spare_parts', label: 'Spare parts' },
  { value: 'accessories', label: 'Accessories & fittings' },
  { value: 'tyres_wheels', label: 'Tyres & wheels' },
  { value: 'lubricants', label: 'Lubricants & fluids' },
  { value: 'batteries', label: 'Batteries' },
  { value: 'electricals', label: 'Electricals' },
  { value: 'body_parts', label: 'Body parts & panels' },
  { value: 'workshop_services', label: 'Workshop / repair services' },
  { value: 'multi_category', label: 'Multi-category auto store' },
  { value: 'other', label: 'Other' },
];

export function getBusinessSpecificationOption(value) {
  return BUSINESS_SPECIFICATIONS.find((t) => t.value === value)
    || GARAGE_SPECIFICATIONS.find((t) => t.value === value)
    || null;
}

export function normalizeBusinessSpecifications(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === 'string' && value.trim()) return [value.trim()];
  return [];
}

export function getBusinessSpecificationLabels(form) {
  const specs = normalizeBusinessSpecifications(form?.businessSpecifications ?? form?.businessSpecification);
  const labels = specs
    .filter((s) => s !== 'other')
    .map((s) => getBusinessSpecificationOption(s)?.label)
    .filter(Boolean);
  if (specs.includes('other')) {
    const other = String(form?.businessSpecificationOther || '').trim();
    labels.push(other || 'Other');
  }
  return labels;
}

export function getBusinessSpecificationLabel(form) {
  return getBusinessSpecificationLabels(form).join(', ');
}

export const EMPTY_BUSINESS_FORM = {
  latitude: '',
  longitude: '',
  locationPinCode: '',
  locationPostOffice: '',
  locationAccuracyM: '',
  capturedLocation: '',
  /** Client-only: 'fallback' when address came from search/map (not sent to API). */
  locationResolvedBy: '',
  houseNo: '',
  street: '',
  landmark: '',
  city: '',
  state: '',
  pinCode: '',
  companyName: '',
  ownerName: '',
  aadhaarNumber: '',
  panNumber: '',
  shopName: '',
  shopNumber: '',
  garageAadhaarNumber: '',
  gstNumber: '',
  companyType: '',
  businessSpecifications: [],
  businessSpecificationOther: '',
  selectedClusters: [],
  fuelSpecialistTypes: [],
  selectedCapabilityTags: [],
  selectedProjectTags: [],
  fulfillmentFlags: {
    doorstep: false,
    pickupDrop: false,
    roadsideAssistance: false,
    sameDayTurnaround: false,
  },
  registrationCertificate: null,
  panCardDocument: null,
  bankPassbook: null,
  aadhaarFront: null,
  shopPicture: null,
  streetPicture: null,
  bankAccountHolder: '',
  bankAccountNumber: '',
  bankIfsc: '',
  bankName: '',
};

const PIN_RE = /^\d{6}$/;

export function isValidIndianPinFormat(pin) {
  const normalized = normalizePin(pin);
  return PIN_RE.test(normalized) && normalized[0] !== '0';
}

export function normalizePin(value) {
  const digits = String(value || '').replace(/\D/g, '');
  return digits.length === 6 ? digits : '';
}

export function composeFullAddress(form) {
  return [form.houseNo, form.street, form.landmark, form.city, form.pinCode]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(', ');
}

export function hasMinimumShopAddress(form) {
  return Boolean(
    form?.houseNo?.trim() &&
    form?.street?.trim() &&
    form?.city?.trim() &&
    normalizePin(form?.pinCode).length === 6
  );
}

export async function geocodeShopAddress(form) {
  const query = composeFullAddress(form);
  if (!hasMinimumShopAddress(form)) return null;

  const searchQuery = `${query}, India`;

  try {
    const nominatimRes = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(searchQuery)}&format=json&limit=1&countrycodes=in`,
      { headers: { Accept: 'application/json', 'User-Agent': 'SparePE-PartnerSignup/1.0' } }
    );
    if (nominatimRes.ok) {
      const results = await nominatimRes.json();
      const hit = results?.[0];
      const lat = Number(hit?.lat);
      const lng = Number(hit?.lon);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        return { lat, lng, label: hit.display_name || query, source: 'address' };
      }
    }
  } catch {
    // try photon
  }

  try {
    const photonRes = await fetch(
      `https://photon.komoot.io/api/?q=${encodeURIComponent(searchQuery)}&limit=1&lang=en`
    );
    if (photonRes.ok) {
      const data = await photonRes.json();
      const feature = data?.features?.[0];
      const coords = feature?.geometry?.coordinates;
      const lng = Number(coords?.[0]);
      const lat = Number(coords?.[1]);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        const props = feature?.properties || {};
        const label = [props.name, props.street, props.city, props.state]
          .filter(Boolean)
          .join(', ') || query;
        return { lat, lng, label, source: 'address' };
      }
    }
  } catch {
    // no result
  }

  return null;
}

export async function resolveShopCoordinatesFromForm(form) {
  const addressHit = await geocodeShopAddress(form);
  if (addressHit) return addressHit;

  const pin = normalizePin(form?.pinCode);
  if (pin.length === 6) {
    const coords = await lookupPincodeCoordinates(pin);
    if (coords && isValidGeoCoord(coords.lat, coords.lng)) {
      return {
        lat: coords.lat,
        lng: coords.lng,
        label: `Near PIN ${pin}`,
        source: 'pin',
      };
    }
  }

  return null;
}

export function hasPinCodeMismatch(form) {
  const livePin = normalizePin(form.locationPinCode);
  const manualPin = normalizePin(form.pinCode);
  if (!livePin || !manualPin) return false;
  return livePin !== manualPin;
}

export function pinMatchesDetectedLocation(form) {
  const livePin = normalizePin(form.locationPinCode);
  const manualPin = normalizePin(form.pinCode);
  if (!livePin || manualPin.length !== 6) return false;
  return livePin === manualPin;
}

export function isShopGpsUnreliable(form) {
  // Desktop/laptop browsers almost never have a GPS chip. Their "live" location
  // comes from IP or Wi-Fi positioning, which can be 50–200 km away from the
  // real location AND often reports an optimistically small accuracy value
  // (well under SHOP_GPS_UNRELIABLE_ACCURACY_M). Because of that, the browser
  // location cannot be trusted to gate PIN validation on desktop — comparing it
  // to the manually entered PIN produces false mismatches. So on desktop we
  // always treat GPS as unreliable and instead verify the entered PIN against
  // the India Post postal database (PIN → district/state). Mobile keeps the
  // original accuracy-based check so real GPS fixes drive strict validation.
  if (isDesktopBrowser()) return true;
  const accuracy = Number(form?.locationAccuracyM);
  return Number.isFinite(accuracy) && accuracy > SHOP_GPS_UNRELIABLE_ACCURACY_M;
}

export function cityMatchesPinDistrict(city, district) {
  const c = String(city || '').trim().toLowerCase();
  const d = String(district || '').trim().toLowerCase();
  if (!c || !d) return false;
  return c === d || c.includes(d) || d.includes(c);
}

export function getPinLocationCheck(form) {
  const manualPin = normalizePin(form.pinCode);
  const livePin = normalizePin(form.locationPinCode);
  const liveOffice = String(form.locationPostOffice || '').trim();
  const liveLabel = liveOffice ? `${livePin} (${liveOffice})` : livePin;

  if (manualPin.length !== 6) {
    return { status: 'idle', message: '' };
  }

  if (isShopGpsUnreliable(form)) {
    if (!form.locationAccuracyM) {
      return { status: 'error', message: 'Tap Use live location first.' };
    }
    if (!String(form.city || '').trim()) {
      return { status: 'error', message: 'Enter your city to verify PIN.' };
    }
    return { status: 'checking', message: 'Verifying PIN against your city…' };
  }

  if (!livePin) {
    return {
      status: 'error',
      message: 'Tap Use live location first to verify your PIN.',
    };
  }
  if (manualPin === livePin) {
    return {
      status: 'valid',
      message: liveOffice
        ? `PIN matches live location — ${livePin}, ${liveOffice}.`
        : `PIN matches live location (${livePin}).`,
    };
  }
  return {
    status: 'invalid',
    message: `Entered PIN (${manualPin}) does not match live location PIN (${liveLabel}).`,
  };
}

export async function lookupStateForPin(pin) {
  const result = await validateIndianPincode(pin);
  if (result.valid === true && result.state) return result.state;
  return '';
}

export async function verifyPinLocationCheck(form) {
  const manualPin = normalizePin(form.pinCode);
  if (manualPin.length !== 6) {
    return { status: 'idle', message: '' };
  }

  if (isShopGpsUnreliable(form)) {
    logShopGeo('pin', 'Verifying PIN against city (manual/GPS-unreliable mode)', {
      pin: manualPin,
      city: String(form.city || '').trim(),
      locationAccuracyM: form.locationAccuracyM,
    });
    if (!form.locationAccuracyM) {
      return { status: 'error', message: 'Tap Use live location first.' };
    }
    const city = String(form.city || '').trim();
    if (!city) {
      return { status: 'error', message: 'Enter your city to verify PIN.' };
    }
    const result = await validateIndianPincode(manualPin);
    if (result.valid !== true) {
      logShopGeo('pin', 'PIN lookup failed', { pin: manualPin, result });
      return {
        status: result.valid === false ? 'invalid' : 'error',
        message: result.message || 'Could not verify PIN.',
      };
    }
    const cityMatch = cityMatchesPinDistrict(city, result.district);
    logShopGeo('pin', 'PIN vs city check', {
      pin: manualPin,
      city,
      district: result.district,
      state: result.state,
      cityMatch,
    });
    if (!cityMatch) {
      return {
        status: 'invalid',
        message: `PIN ${manualPin} belongs to ${result.district}, ${result.state} — does not match city "${city}".`,
      };
    }
    return {
      status: 'valid',
      message: `PIN verified for ${result.district}, ${result.state}.`,
      state: result.state || '',
    };
  }

  const check = getPinLocationCheck(form);
  logShopGeo('pin', 'PIN vs live location check', { pin: manualPin, check });
  if (check.status === 'valid') {
    const state = await lookupStateForPin(manualPin);
    return { ...check, state };
  }
  return check;
}

function pickBestPostOffice(offices, addr, displayName, lat, lng, pin) {
  if (!offices.length) return null;
  const regional = offices.filter((office) => officeMatchesRegion(office, addr));
  const pool = regional.length ? regional : offices;
  const { allTerms } = extractAreaSearchTerms(addr, displayName);
  const terms = allTerms.length ? allTerms : [''];

  let bestOffice = null;
  let bestScore = -1;

  for (const office of pool) {
    for (const term of terms) {
      const { score } = scorePinCandidate(pin, office, addr, lat, lng, term);
      if (score > bestScore) {
        bestScore = score;
        bestOffice = office;
      }
    }
  }

  if (bestOffice && bestScore >= 30) return bestOffice;

  return (
    pool.find((office) => String(office?.DeliveryStatus || '').toLowerCase() === 'delivery') ||
    pool[0] ||
    null
  );
}

export async function reverseGeocodePinFromCoords(lat, lng) {
  const [nominatimResult, photonAddr, meteoAddr] = await Promise.all([
    fetchNominatimAddress(lat, lng),
    fetchPhotonAddress(lat, lng),
    fetchOpenMeteoAddress(lat, lng),
  ]);
  const usePhotonStreet = photonAddr.road && !photonAddr.isPoi;
  const addr = {
    ...nominatimResult.address,
    road: nominatimResult.address.road || (usePhotonStreet ? photonAddr.road : ''),
    neighbourhood:
      nominatimResult.address.neighbourhood ||
      meteoAddr.neighbourhood ||
      (!photonAddr.isPoi ? photonAddr.neighbourhood : '') ||
      '',
    suburb: nominatimResult.address.suburb || photonAddr.suburb || '',
    district:
      nominatimResult.address.city_district ||
      nominatimResult.address.district ||
      (!photonAddr.isPoi ? photonAddr.district : '') ||
      meteoAddr.district ||
      '',
    city: nominatimResult.address.city || photonAddr.city || meteoAddr.city || '',
    state: nominatimResult.address.state || photonAddr.state || meteoAddr.state || '',
    postcode: nominatimResult.address.postcode || photonAddr.postcode || '',
  };
  const city = pickCityFromAddress(addr);
  const { pinCode, postOfficeName } = await resolvePinFromCoords(
    addr,
    nominatimResult.displayName,
    lat,
    lng,
    photonAddr
  );
  const locationLabel = formatCapturedLocationLabel(
    addr,
    nominatimResult.displayName || meteoAddr.displayName,
    photonAddr,
    postOfficeName
  );
  return { pinCode, city, state: String(addr.state || '').trim(), locationLabel, postOfficeName };
}

async function fetchNominatimAddress(lat, lng) {
  const response = await fetch(
    `https://nominatim.openstreetmap.org/reverse?lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lng)}&zoom=19&format=jsonv2&addressdetails=1`,
    { headers: { Accept: 'application/json', 'User-Agent': 'SparePE-PartnerSignup/1.0' } }
  );
  if (!response.ok) throw new Error('Could not resolve PIN from location');
  const data = await response.json();
  return { address: data?.address || {}, displayName: data?.display_name || '' };
}

const POI_OSM_KEYS = new Set([
  'shop', 'amenity', 'tourism', 'leisure', 'office', 'craft', 'healthcare', 'man_made',
]);

async function fetchPhotonAddress(lat, lng) {
  try {
    const response = await fetch(
      `https://photon.komoot.io/reverse?lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lng)}`
    );
    if (!response.ok) return { isPoi: false };
    const data = await response.json();
    const features = Array.isArray(data?.features) ? data.features : [];
    const props = pickPhotonProperties(features);
    const isPoi = POI_OSM_KEYS.has(String(props.osm_key || '').toLowerCase());
    return {
      road: props.street || '',
      neighbourhood: props.locality || '',
      suburb: props.district || '',
      district: props.district || '',
      city: props.city || '',
      state: props.state || '',
      postcode: props.postcode || '',
      isPoi,
      osmKey: props.osm_key || '',
    };
  } catch {
    return { isPoi: false };
  }
}

function pickPhotonProperties(features = []) {
  const preferKeys = new Set(['highway', 'place', 'building', 'landuse', 'residential']);
  for (const feature of features) {
    const props = feature?.properties || {};
    const key = String(props.osm_key || '').toLowerCase();
    if (preferKeys.has(key)) return props;
  }
  for (const feature of features) {
    const props = feature?.properties || {};
    const key = String(props.osm_key || '').toLowerCase();
    if (!POI_OSM_KEYS.has(key)) return props;
  }
  return features[0]?.properties || {};
}

async function fetchOpenMeteoAddress(lat, lng) {
  try {
    const response = await fetch(
      `https://geocoding-api.open-meteo.com/v1/reverse?latitude=${encodeURIComponent(lat)}&longitude=${encodeURIComponent(lng)}&language=en&count=1`
    );
    if (!response.ok) return {};
    const data = await response.json();
    const result = data?.results?.[0] || data;
    if (!result) return {};
    const neighbourhood = String(result.name || '').trim();
    const city = String(result.admin2 || result.admin3 || '').trim();
    const state = String(result.admin1 || '').trim();
    return {
      neighbourhood,
      district: city,
      city: city || neighbourhood,
      state,
      displayName: [neighbourhood, city, state].filter(Boolean).join(', '),
    };
  } catch {
    return {};
  }
}

function pickCityFromAddress(addr = {}) {
  return (
    addr.city ||
    addr.town ||
    addr.village ||
    addr.suburb ||
    addr.state_district ||
    addr.county ||
    ''
  );
}

export function dedupeCommaLabel(label) {
  const seen = [];
  String(label || '')
    .split(',')
    .map((segment) => segment.trim())
    .filter(Boolean)
    .forEach((segment) => {
      if (!seen.some((part) => part.toLowerCase() === segment.toLowerCase())) {
        seen.push(segment);
      }
    });
  return seen.join(', ');
}

export function formatCapturedLocationLabel(addr = {}, displayName = '', photonAddr = {}, postOfficeName = '') {
  return dedupeCommaLabel(
    buildCapturedLocationLabel(addr, displayName, photonAddr, postOfficeName)
  );
}

function buildCapturedLocationLabel(addr = {}, displayName = '', photonAddr = {}, postOfficeName = '') {
  const nominatimLabel = formatNominatimDisplayName(displayName);
  const hasNominatimRoad = Boolean(String(addr.road || '').trim());
  const photonIsPoi = Boolean(photonAddr?.isPoi);

  if (!hasNominatimRoad || photonIsPoi) {
    if (postOfficeName) {
      const city = pickCityFromAddress(addr) || photonAddr.city || '';
      const state = String(addr.state || photonAddr.state || '').trim();
      const parts = [];
      [postOfficeName, city, state].forEach((value) => {
        const text = String(value || '').trim();
        if (!text) return;
        if (parts.some((part) => part.toLowerCase() === text.toLowerCase())) return;
        parts.push(text);
      });
      if (parts.length >= 2) return parts.join(', ');
    }
    if (nominatimLabel) return nominatimLabel;
  }

  const road = String(addr.road || '').trim();
  const district = String(addr.district || addr.city_district || '')
    .replace(/\s+zone\s+\d+$/i, '')
    .trim();
  const locality = String(
    postOfficeName || addr.neighbourhood || photonAddr.neighbourhood || ''
  ).trim();
  const suburb = String(addr.suburb || '').replace(/\s+ward$/i, '').trim();
  const city = pickCityFromAddress(addr) || photonAddr.city || '';
  const state = String(addr.state || photonAddr.state || '').trim();

  const parts = [];
  const push = (value) => {
    const text = String(value || '').trim();
    if (!text) return;
    const lower = text.toLowerCase();
    if (parts.some((part) => part.toLowerCase() === lower)) return;
    parts.push(text);
  };

  if (road) push(road);
  if (district && district.toLowerCase() !== city.toLowerCase()) push(district);
  else if (locality) push(locality);
  else if (suburb) push(suburb);
  if (city) push(city);
  if (state && state.toLowerCase() !== city.toLowerCase()) push(state);

  if (parts.length >= 2) return parts.join(', ');
  if (parts.length === 1) return parts[0];
  return nominatimLabel || 'Location captured';
}

function formatNominatimDisplayName(displayName = '') {
  const parts = String(displayName)
    .split(',')
    .map((segment) => segment.trim())
    .filter(Boolean)
    .filter((segment) => !/^(india|ind)$/i.test(segment))
    .filter((segment) => !/^\d{6}$/.test(segment))
    .filter((segment) => !/mumbai city district/i.test(segment))
    .filter((segment) => !/zone \d+/i.test(segment));

  const unique = [];
  for (const part of parts) {
    if (!unique.some((item) => item.toLowerCase() === part.toLowerCase())) unique.push(part);
  }
  return unique.slice(0, 4).join(', ');
}

function normalizeDistrict(value = '') {
  return String(value || '')
    .replace(/\s+(city\s+)?district$/i, '')
    .replace(/\s+zone\s+\d+$/i, '')
    .trim();
}

function officeMatchesRegion(office, addr) {
  const state = String(addr.state || '').trim().toLowerCase();
  const district = normalizeDistrict(addr.state_district || addr.county || addr.city || '').toLowerCase();
  const officeState = String(office?.State || '').trim().toLowerCase();
  const officeDistrict = normalizeDistrict(office?.District || '').toLowerCase();
  if (!state || !officeState || officeState !== state) return false;
  if (!district) return true;
  return officeDistrict === district || officeDistrict.includes(district) || district.includes(officeDistrict);
}

function extractAreaSearchTerms(addr = {}, displayName = '') {
  const roadTokens = [];
  const otherTerms = [];
  const add = (value, bucket = otherTerms) => {
    const text = String(value || '').trim();
    if (!text) return;
    if (!bucket.includes(text)) bucket.push(text);
    const withoutWard = text.replace(/\s+ward$/i, '').trim();
    if (withoutWard.length > 2 && !bucket.includes(withoutWard)) bucket.push(withoutWard);
  };

  const road = String(addr.road || '');
  road
    .split(/\s+(?:road|rd|marg|street|st|link|highway|flyover|bridge)\b/i)[0]
    .split(/\s+/)
    .filter((part) => part.length > 3 && !/^(link|road|the|and)$/i.test(part))
    .forEach((part) => add(part, roadTokens));

  add(addr.neighbourhood);
  add(addr.district);
  add(addr.suburb);
  add(addr.city_district);
  add(addr.quarter);
  add(addr.hamlet);

  String(displayName || '')
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 3 && !/^(india|maharashtra|mumbai)$/i.test(part) && !/^\d{6}$/.test(part))
    .slice(0, 5)
    .forEach((part) => add(part));

  return { roadTokens, allTerms: [...roadTokens, ...otherTerms].slice(0, 12) };
}

async function fetchPincodeOffices(pin) {
  const normalized = normalizePin(pin);
  if (!normalized) return [];
  try {
    const response = await fetch(`https://api.postalpincode.in/pincode/${normalized}`);
    if (!response.ok) return [];
    const payload = await response.json();
    const entry = Array.isArray(payload) ? payload[0] : payload;
    return Array.isArray(entry?.PostOffice) ? entry.PostOffice : [];
  } catch {
    return [];
  }
}

async function fetchPostOfficesByName(name) {
  const query = String(name || '').trim();
  if (!query) return [];
  try {
    const response = await fetch(`https://api.postalpincode.in/postoffice/${encodeURIComponent(query)}`);
    if (!response.ok) return [];
    const payload = await response.json();
    const entry = Array.isArray(payload) ? payload[0] : payload;
    return Array.isArray(entry?.PostOffice) ? entry.PostOffice : [];
  } catch {
    return [];
  }
}

function scorePinCandidate(pin, office, addr, lat, lng, searchTerm = '') {
  let score = 0;
  const neighbourhood = String(addr.neighbourhood || '').trim().toLowerCase();
  const officeName = String(office?.Name || '').trim().toLowerCase();
  const suburb = String(addr.suburb || '').trim().toLowerCase();
  const term = String(searchTerm || '').trim().toLowerCase();

  if (term && officeName === term) score += 80;
  else if (term && (officeName.includes(term) || term.includes(officeName))) score += 45;

  if (neighbourhood && officeName === neighbourhood) score += 120;
  else if (neighbourhood && (officeName.includes(neighbourhood) || neighbourhood.includes(officeName))) score += 70;

  if (suburb && officeName.includes(suburb.replace(/\s+ward$/, ''))) score += 30;

  const district = String(addr.district || '').trim().toLowerCase();
  if (district && officeName === district) score += 35;

  const road = String(addr.road || '').toLowerCase();
  if (road && officeName && road.includes(officeName)) score += 50;

  const olat = parseCoord(office?.Latitude);
  const olng = parseCoord(office?.Longitude);
  if (isValidGeoCoord(olat, olng) && isValidGeoCoord(lat, lng)) {
    const distance = haversineDistanceKm(lat, lng, olat, olng);
    if (distance != null) score += Math.max(0, 40 - distance * 4);
  }

  if (String(office?.DeliveryStatus || '').toLowerCase() === 'delivery') score += 10;

  return { pin, score, officeName: office?.Name || '' };
}

async function resolvePinFromCoords(addr, displayName, lat, lng, photonAddr = {}) {
  const photonPin = normalizePin(photonAddr.postcode);
  const osmPin =
    normalizePin(String(addr.postcode || '').match(/\d{6}/)?.[0] || '') ||
    normalizePin(String(displayName || '').match(/\b\d{6}\b/)?.[0] || '');
  const pinCandidates = [...new Set([photonPin, osmPin].filter(Boolean))];

  for (const pin of pinCandidates) {
    const offices = await fetchPincodeOffices(pin);
    if (!offices.length) continue;
    const office = pickBestPostOffice(offices, addr, displayName, lat, lng, pin);
    if (!office) continue;
    return {
      pinCode: pin,
      postOfficeName: String(office?.Name || '').trim(),
    };
  }

  const { roadTokens, allTerms: searchTerms } = extractAreaSearchTerms(addr, displayName);
  const scored = [];
  const seen = new Set();

  for (const term of searchTerms.slice(0, 6)) {
    const offices = await fetchPostOfficesByName(term);
    const fromRoad = roadTokens.includes(term);
    for (const office of offices) {
      if (!officeMatchesRegion(office, addr)) continue;
      const pin = normalizePin(office.Pincode);
      if (!pin) continue;
      const key = `${pin}:${office.Name}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const candidate = scorePinCandidate(pin, office, addr, lat, lng, term);
      if (candidate.score >= 50) scored.push({ ...candidate, fromRoad });
    }
  }

  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    if (b.fromRoad !== a.fromRoad) return Number(b.fromRoad) - Number(a.fromRoad);
    return 0;
  });

  const best = scored[0];
  if (best) {
    return { pinCode: best.pin, postOfficeName: best.officeName };
  }

  return { pinCode: pinCandidates[0] || '', postOfficeName: '' };
}

const DRAFT_FILE_KEYS = [
  'registrationCertificate',
  'panCardDocument',
  'bankPassbook',
  'aadhaarFront',
  'shopPicture',
  'streetPicture',
];

function sanitizeLoadedFileField(field) {
  if (!field || typeof field !== 'object') return field;
  if (!field.base64 && !field.dataUrl) return field;
  return {
    fileName: field.fileName || '',
    previewName: field.previewName || field.fileName || '',
    mimeType: field.mimeType || '',
    uploadedUrl: field.uploadedUrl || '',
    storageKey: field.storageKey || '',
    storedInDb: Boolean(field.storedInDb),
  };
}

export function hasPartnerFileField(field) {
  if (!field) return false;
  if (typeof field === 'string') return field.startsWith('http');
  if (field.uploadedUrl) return true;
  if (field.storedInDb && field.storageKey) return true;
  if (field.base64) return true;
  return Boolean(field.previewName || field.fileName);
}

export function loadBusinessDraft() {
  try {
    const raw = localStorage.getItem(PARTNER_BUSINESS_DRAFT_KEY);
    if (!raw) return { ...EMPTY_BUSINESS_FORM };
    if (raw.length > 500_000) {
      localStorage.removeItem(PARTNER_BUSINESS_DRAFT_KEY);
      return { ...EMPTY_BUSINESS_FORM };
    }
    const parsed = { ...EMPTY_BUSINESS_FORM, ...JSON.parse(raw) };
    DRAFT_FILE_KEYS.forEach((key) => {
      parsed[key] = sanitizeLoadedFileField(parsed[key]);
    });
    if (parsed.companyType === 'proprietorship') {
      parsed.companyType = 'sole_proprietorship';
    }
    if (parsed.companyType === 'public_limited') {
      parsed.companyType = 'private_limited';
    }
    if (parsed.companyType === 'medium_scrapyard') parsed.companyType = 'medium_garage';
    if (parsed.companyType === 'large_scrapyard') parsed.companyType = 'large_garage';
    if (parsed.companyType === 'shop_sparepart') parsed.companyType = 'small_garage';
    parsed.businessSpecifications = normalizeBusinessSpecifications(
      parsed.businessSpecifications ?? parsed.businessSpecification
    );
    parsed.selectedClusters = Array.isArray(parsed.selectedClusters)
      ? parsed.selectedClusters.map(Number).filter((n) => n >= 1 && n <= 10)
      : [];
    parsed.fuelSpecialistTypes = Array.isArray(parsed.fuelSpecialistTypes) ? parsed.fuelSpecialistTypes : [];
    parsed.selectedCapabilityTags = Array.isArray(parsed.selectedCapabilityTags) ? parsed.selectedCapabilityTags : [];
    parsed.selectedProjectTags = Array.isArray(parsed.selectedProjectTags) ? parsed.selectedProjectTags : [];
    parsed.fulfillmentFlags = {
      doorstep: false,
      pickupDrop: false,
      roadsideAssistance: false,
      sameDayTurnaround: false,
      ...(parsed.fulfillmentFlags && typeof parsed.fulfillmentFlags === 'object' ? parsed.fulfillmentFlags : {}),
    };
    if (parsed.companyType === 'transformal') parsed.companyType = 'transformer';
    delete parsed.businessSpecification;
    return parsed;
  } catch {
    return { ...EMPTY_BUSINESS_FORM };
  }
}

function stripDraftFilePayloads(data) {
  const slim = { ...data };
  DRAFT_FILE_KEYS.forEach((key) => {
    const file = slim[key];
    if (!file || typeof file !== 'object') return;
    slim[key] = {
      fileName: file.fileName || '',
      previewName: file.previewName || file.fileName || '',
      mimeType: file.mimeType || '',
      uploadedUrl: file.uploadedUrl || '',
      storageKey: file.storageKey || key,
      storedInDb: Boolean(file.storedInDb),
    };
  });
  return slim;
}

export function saveBusinessDraft(data) {
  try {
    // Never persist base64 blobs — they exceed localStorage limits and cause low-memory errors on mobile.
    localStorage.setItem(PARTNER_BUSINESS_DRAFT_KEY, JSON.stringify(stripDraftFilePayloads(data)));
  } catch {
    // Draft metadata failed; in-memory form state still works for this session.
  }
}

export function clearBusinessDraft() {
  localStorage.removeItem(PARTNER_BUSINESS_DRAFT_KEY);
  localStorage.removeItem(PARTNER_SIGNUP_STEP_KEY);
  sessionStorage.removeItem(PARTNER_SIGNUP_SUB_STEP_KEY);
  sessionStorage.removeItem(PARTNER_SIGNUP_SESSION_KEY);
  import('./partnerSignupFileStore').then(({ clearAllPartnerFiles }) => clearAllPartnerFiles()).catch(() => {});
}

export function isRegisteredCompanyType(companyType) {
  return ['partnership', 'private_limited', 'llp', 'opc'].includes(companyType);
}

export function isInformalCompanyType(companyType) {
  return companyType === 'sole_proprietorship';
}

export function isOtherCompanyType(companyType) {
  return companyType === 'other';
}

export function isGarageCompanyType(companyType) {
  return GARAGE_COMPANY_TYPES.some((t) => t.value === companyType);
}

export function isSellerCompanySignup(companyType, accountMainType = '') {
  if (accountMainType === 'garage' || isGarageCompanyType(companyType)) return false;
  return Boolean(companyType) && COMPANY_TYPES.some((t) => t.value === companyType);
}

const AADHAAR_RE = /^\d{12}$/;
const PAN_RE = /^[A-Z]{5}\d{4}[A-Z]$/i;
const GST_RE = /^[0-9]{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/i;
const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/i;
const SHOP_PHONE_RE = /^\d{10}$/;

export function validateBusinessSubStep(subStep, form, accountMainType = '') {
  const isGarage = accountMainType === 'garage' || isGarageCompanyType(form.companyType);
  const placeLabel = isGarage ? 'garage' : 'shop';

  if (subStep === 0) {
    if (!form.houseNo?.trim()) return `House / ${placeLabel} number is required`;
    if (!form.street?.trim()) return 'Street is required';
    if (!form.city?.trim()) return 'City is required';
    if (!isValidIndianPinFormat(form.pinCode)) return `Enter a valid 6-digit ${placeLabel} PIN code`;
    if (!form.locationAccuracyM && !normalizePin(form.locationPinCode)) {
      return `Tap Use live location to verify your ${placeLabel} PIN`;
    }
    if (isShopGpsUnreliable(form)) {
      if (!String(form.city || '').trim()) return 'City is required to verify your PIN';
      return '';
    }
    if (!normalizePin(form.locationPinCode)) {
      return `Tap Use live location to verify your ${placeLabel} PIN`;
    }
    const pinCheck = getPinLocationCheck(form);
    if (pinCheck.status === 'invalid' || pinCheck.status === 'error') {
      return pinCheck.message;
    }
    if (!isValidGeoCoord(form.latitude, form.longitude)) {
      return `Tap Use live location to capture your ${placeLabel} GPS`;
    }
    return '';
  }

  if (subStep === 1) {
    if (isGarage) {
      if (!form.shopName?.trim()) return 'Garage name is required';
      if (!SHOP_PHONE_RE.test(form.shopNumber || '')) return 'Enter a valid 10-digit garage phone number';
      if (!form.ownerName?.trim()) return 'Owner name is required';
      if (!AADHAAR_RE.test(form.aadhaarNumber || '')) return 'Enter a valid 12-digit owner Aadhaar number';
      if (!PAN_RE.test(form.panNumber || '')) return 'Enter a valid PAN (e.g. ABCDE1234F)';
      if (!form.companyType) return 'Garage type missing — go back and select your garage type';

      const tier = normalizeGarageTier(form.companyType);
      if (tier === 'custom_workshop') {
        const tags = Array.isArray(form.selectedCapabilityTags) ? form.selectedCapabilityTags : [];
        if (!tags.length) return 'Select at least one capability tag';
      } else if (tier === 'transformer') {
        const tags = Array.isArray(form.selectedProjectTags) ? form.selectedProjectTags : [];
        if (!tags.length) return 'Select at least one project type';
      } else if (tier) {
        const resolvedFuel = resolveFuelSpecialistTypes(
          tier,
          form.selectedClusters,
          form.fuelSpecialistTypes
        );
        const clusterErrors = validateClusterSelection(
          tier,
          form.selectedClusters,
          resolvedFuel
        );
        if (clusterErrors.length) return clusterErrors[0];
        if (
          fuelSpecialistSelectionRequired(tier, form.selectedClusters) &&
          !resolvedFuel.length
        ) {
          return 'Select CNG/LPG and/or EV/Hybrid for Fuel-Specialist cluster';
        }
      }
    } else {
      if (!form.companyType) return 'Please select company type';
      if (!form.shopName?.trim()) return 'Shop name is required';
      if (isRegisteredCompanyType(form.companyType) && !form.companyName?.trim()) {
        return 'Legal company name is required';
      }
      if (!form.ownerName?.trim()) return 'Owner name is required';
      if (!PAN_RE.test(form.panNumber || '')) return 'Enter a valid PAN (e.g. ABCDE1234F)';
      if (!SHOP_PHONE_RE.test(form.shopNumber || '')) return 'Enter a valid 10-digit shop phone number';
      if (form.gstNumber && !GST_RE.test(form.gstNumber)) return 'Enter a valid GST number or leave blank';
    }
    const specs = normalizeBusinessSpecifications(form.businessSpecifications);
    if (!isGarage) {
      if (!specs.length) return 'Please select at least one category';
      if (specs.includes('other') && !form.businessSpecificationOther?.trim()) {
        return 'Please describe your other category';
      }
    }
    return '';
  }

  if (subStep === 2) {
    if (isRegisteredCompanyType(form.companyType)) {
      if (!form.gstNumber?.trim()) return 'GST number is mandatory for this company type';
      if (!GST_RE.test(form.gstNumber)) return 'Enter a valid GST number';
    } else {
      if (!form.gstNumber?.trim()) return 'GST number is required';
      if (!GST_RE.test(form.gstNumber)) return 'Enter a valid GST number';
    }

    if (!form.bankAccountHolder?.trim()) return 'Account holder name is required';
    if (!form.bankAccountNumber?.trim()) return 'Bank account number is required';
    if (!form.bankName?.trim()) return 'Bank name is required';
    if (!IFSC_RE.test(form.bankIfsc || '')) return 'Enter a valid IFSC code';

    // Documents & photos — captured/uploaded for every company type
    if (isRegisteredCompanyType(form.companyType) && !hasPartnerFileField(form.registrationCertificate)) {
      return 'Registration certificate is required';
    }
    if (!hasPartnerFileField(form.shopPicture)) return 'Shop picture is required';
    if (!hasPartnerFileField(form.streetPicture)) return 'Street picture is required';

    if (isSellerCompanySignup(form.companyType, accountMainType)) {
      if (!hasPartnerFileField(form.bankPassbook)) return 'Bank passbook picture is required';
      if (!hasPartnerFileField(form.aadhaarFront)) return 'Aadhaar card picture is required';
      if (!hasPartnerFileField(form.panCardDocument)) return 'PAN card picture is required';
    }
    return '';
  }

  return '';
}

const BUSINESS_ERROR_FIELD_RULES = [
  { test: /house.*garage number/i, fieldId: 'houseNo' },
  { test: /house.*shop number/i, fieldId: 'houseNo' },
  { test: /street/i, fieldId: 'street' },
  { test: /city/i, fieldId: 'city' },
  { test: /PIN|live location|GPS/i, fieldId: 'pinCode' },
  { test: /legal company name/i, fieldId: 'companyName' },
  { test: /company name/i, fieldId: 'companyName' },
  { test: /garage name/i, fieldId: 'shopName' },
  { test: /garage phone/i, fieldId: 'shopNumber' },
  { test: /owner name/i, fieldId: 'ownerName' },
  { test: /owner Aadhaar/i, fieldId: 'aadhaarNumber' },
  { test: /PAN \(e\.g/i, fieldId: 'panNumber' },
  { test: /shop name/i, fieldId: 'shopName' },
  { test: /shop phone|shop number/i, fieldId: 'shopNumber' },
  { test: /garage type/i, fieldId: 'companyType' },
  { test: /company type/i, fieldId: 'companyType' },
  { test: /categor|specification/i, fieldId: 'businessSpecifications' },
  { test: /cluster/i, fieldId: 'selectedClusters' },
  { test: /capability tag/i, fieldId: 'selectedCapabilityTags' },
  { test: /project type/i, fieldId: 'selectedProjectTags' },
  { test: /CNG|LPG|EV|Hybrid|Fuel-Specialist/i, fieldId: 'selectedClusters' },
  { test: /other categor/i, fieldId: 'businessSpecificationOther' },
  { test: /registration certificate/i, fieldId: 'registrationCertificate' },
  { test: /PAN card (document|picture)/i, fieldId: 'panCardDocument' },
  { test: /bank passbook/i, fieldId: 'bankPassbook' },
  { test: /aadhaar card picture/i, fieldId: 'aadhaarFront' },
  { test: /shop picture/i, fieldId: 'shopPicture' },
  { test: /street picture/i, fieldId: 'streetPicture' },
  { test: /GST/i, fieldId: 'gstNumber' },
  { test: /account holder/i, fieldId: 'bankAccountHolder' },
  { test: /bank account number/i, fieldId: 'bankAccountNumber' },
  { test: /IFSC/i, fieldId: 'bankIfsc' },
  { test: /bank name/i, fieldId: 'bankName' },
];

export function getBusinessErrorFieldId(message, subStep, form = {}) {
  if (!message) return null;
  for (const rule of BUSINESS_ERROR_FIELD_RULES) {
    if (rule.test.test(message)) {
      if (rule.fieldId === 'gstNumber' && subStep === 2) {
        if (isInformalCompanyType(form.companyType) || isOtherCompanyType(form.companyType) || isGarageCompanyType(form.companyType)) return 'gstOther';
        if (isRegisteredCompanyType(form.companyType)) return 'gstNumberDocs';
      }
      return rule.fieldId;
    }
  }
  return null;
}

export function focusSignupField(fieldId) {
  if (!fieldId || typeof document === 'undefined') return;
  requestAnimationFrame(() => {
    const el = document.getElementById(fieldId);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (typeof el.focus === 'function') {
      try {
        el.focus({ preventScroll: true });
      } catch {
        el.focus();
      }
    }
  });
}

export function showSignupFieldError(setError, message, fieldId) {
  setError(message);
  focusSignupField(fieldId);
}

export async function uploadPartnerFile(field) {
  if (!field) return '';
  if (typeof field === 'string') {
    if (field.startsWith('http') || field.startsWith('/uploads/')) return field;
    return '';
  }
  if (field.uploadedUrl) return field.uploadedUrl;

  const { isCloudinaryDirectConfigured, uploadToCloudinary } = await import('../../utils/cloudinaryUpload');
  const { getPartnerFile, deletePartnerFile } = await import('./partnerSignupFileStore');

  if (field.storedInDb && field.storageKey) {
    const blob = await getPartnerFile(field.storageKey);
    if (!blob) return '';

    const file = blob instanceof File
      ? blob
      : new File([blob], field.fileName || 'upload.jpg', { type: field.mimeType || blob.type || 'image/jpeg' });

    const { prepareStoredUploadFile, uploadPreparedFile, isImageFile, prefersMobileDevice, MAX_RAW_CAMERA_BYTES } = await import('./partnerUploadFile');
    let url = '';
    if (isImageFile(file)) {
      // Mobile: avoid a second canvas decode on submit (OOM / Network Error).
      // Upload the stored JPEG as-is when already within the server size limit.
      if (prefersMobileDevice() && file.size <= MAX_RAW_CAMERA_BYTES) {
        url = await uploadPreparedFile(file);
      } else {
        const prepared = await prepareStoredUploadFile(file, {});
        url = await uploadPreparedFile(prepared);
      }
    } else if (isCloudinaryDirectConfigured()) {
      url = await uploadToCloudinary(file);
    } else {
      const { authService } = await import('../../services/apiService');
      url = await authService.uploadFileBlob(file);
    }
    await deletePartnerFile(field.storageKey).catch(() => {});
    return url;
  }

  if (!field.base64) return '';

  const { authService } = await import('../../services/apiService');
  const url = await authService.uploadFile(field.base64, field.fileName, field.mimeType);
  return url;
}

export function buildPartnerOnboardingPayload(businessData, role, sellerCategory = '', accountSelection = null) {
  const accountMeta = buildAccountTypeMeta(
    accountSelection || {
      mainType: role === 'garage' ? 'garage' : 'sparepart_seller',
      partCondition: role === 'used_pending' ? 'used' : 'new',
      sellerCategory: sellerCategory || '',
    }
  );
  const base = {
    latitude: businessData.latitude,
    longitude: businessData.longitude,
    locationPinCode: normalizePin(businessData.locationPinCode),
    houseNo: businessData.houseNo,
    street: businessData.street,
    landmark: businessData.landmark || '',
    city: businessData.city,
    state: businessData.state || '',
    pinCode: normalizePin(businessData.pinCode),
    pinCodeMismatch: hasPinCodeMismatch(businessData),
    fullAddress: composeFullAddress(businessData),
    companyName: businessData.companyName,
    ownerName: businessData.ownerName,
    contactEmail: businessData.contactEmail || '',
    aadhaarNumber: businessData.aadhaarNumber,
    panNumber: businessData.panNumber,
    garageAadhaarNumber: businessData.garageAadhaarNumber || '',
    shopName: businessData.shopName,
    shopNumber: businessData.shopNumber,
    gstNumber: businessData.gstNumber || '',
    companyType: businessData.companyType,
    businessSpecifications: normalizeBusinessSpecifications(businessData.businessSpecifications),
    businessSpecificationOther: businessData.businessSpecificationOther || '',
    businessSpecificationLabels: getBusinessSpecificationLabels(businessData),
    businessSpecificationLabel: getBusinessSpecificationLabel(businessData),
    registrationCertificate: businessData.registrationCertificateUrl || '',
    panCardDocument: businessData.panCardDocumentUrl || '',
    bankPassbook: businessData.bankPassbookUrl || '',
    aadhaarFront: businessData.aadhaarFrontUrl || '',
    shopPicture: businessData.shopPictureUrl || '',
    streetPicture: businessData.streetPictureUrl || '',
    bankAccountHolder: businessData.bankAccountHolder,
    bankAccountNumber: businessData.bankAccountNumber,
    bankIfsc: businessData.bankIfsc,
    bankName: businessData.bankName,
    verificationStatus: 'pending_review',
    onboardingPending: true,
    ...accountMeta,
    submittedAt: new Date().toISOString(),
  };

  if (role === 'garage') {
    const garageTier = normalizeGarageTier(
      sellerCategory || accountSelection?.sellerCategory || businessData.companyType || ''
    );
    const selectedClusters = (Array.isArray(businessData.selectedClusters)
      ? businessData.selectedClusters
      : []
    ).map(Number).filter((n) => n >= 1 && n <= 10);
    const fuelSpecialistTypes = resolveFuelSpecialistTypes(
      garageTier,
      selectedClusters,
      businessData.fuelSpecialistTypes || []
    );
    const clusterLabels = selectedClusters
      .map((id) => getClusterById(id)?.label)
      .filter(Boolean);
    const expandedServices = previewGarageServices({
      companyType: garageTier || businessData.companyType,
      selectedClusters,
      fuelSpecialistTypes,
    });

    return {
      role: 'garage',
      email: businessData.contactEmail || '',
      verificationStatus: 'pending_review',
      onboardingPending: true,
      garageDetails: {
        ...base,
        garageName: businessData.shopName,
        garageAadhaarNumber: businessData.garageAadhaarNumber || '',
        ownerName: businessData.ownerName,
        ownerAadhaarNumber: businessData.aadhaarNumber,
        ownerPanNumber: businessData.panNumber,
        phone: businessData.shopNumber,
        sellerCategory: sellerCategory || accountSelection?.sellerCategory || businessData.companyType || '',
        garageTier,
        selectedClusters,
        fuelSpecialistTypes,
        selectedCapabilityTags: businessData.selectedCapabilityTags || [],
        selectedProjectTags: businessData.selectedProjectTags || [],
        fulfillmentFlags: businessData.fulfillmentFlags || {},
        bookingMode: getBookingModeForTier(garageTier),
        clusterLabels,
        servicesFromClusters: selectedClusters.length > 0,
        services: expandedServices.length ? expandedServices : undefined,
      },
    };
  }

  return {
    role: role === 'used_pending' ? 'used_pending' : 'vendor',
    email: businessData.contactEmail || '',
    verificationStatus: 'pending_review',
    onboardingPending: true,
    vendorDetails: {
      ...base,
      businessName: businessData.companyName,
      storeName: businessData.shopName,
      phone: businessData.shopNumber,
      sellerCategory,
      partCondition: accountMeta.partCondition || (role === 'used_pending' ? 'used' : 'new'),
    },
  };
}
